import { useState, useEffect } from 'react';
import { useAuth } from '../AuthProvider';

interface Subscription {
  id: string;
  user_id: string;
  plan: string;
  status: string;
  qr_codes_used: number;
  books_used: number;
  campaigns_used: number;
  scans_this_month: number;
  seats_used: number;
  ai_requests_used: number;
  ai_requests_reset_at?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  is_trial?: boolean;
  trial_ends_at?: string;
  team_info?: {
    is_team_member: boolean;
    subscription_owner: string;
    team_size: number;
    user_role: string;
  };
}

interface PlanLimits {
  plan: string;
  qr_codes_limit: number;
  books_limit: number;
  campaigns_limit: number;
  scans_limit: number;
  seats_limit: number;
  ai_requests_limit: number;
}

export function usePlanLimits() {
  const { session } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  useEffect(() => {
    loadPlanData();
  }, [session]);

  const loadPlanData = async () => {
    if (!session?.access_token) {
      console.log('🔍 Plan Limits Debug: No access token available');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 Plan Limits Debug: Loading subscription data (fast mode)');
      
      // 🚀 OPTIMIZATION: Load subscription in fast mode (without heavy usage calculations)
      const subResponse = await fetch(`${API_BASE}/api/billing/subscription`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      console.log('🔍 Plan Limits Debug: Subscription response status:', subResponse.status);

      if (subResponse.ok) {
        const subData = await subResponse.json();
        console.log('🔍 Plan Limits Debug: Subscription data received:', subData);
        setSubscription(subData);
        
        // 🚀 OPTIMIZATION: Use default plan limits (no separate API call needed)
        const defaultLimits = getDefaultPlanLimits(subData.plan);
        console.log('🔍 Plan Limits Debug: Using default limits:', defaultLimits);
        setPlanLimits(defaultLimits);
      } else {
        // Fallback to default subscription if API call fails
        console.warn('🔍 Plan Limits Debug: Subscription API not available, using free plan defaults');
        const defaultSub = {
          id: 'default',
          user_id: 'default', 
          plan: 'free',
          status: 'active',
          qr_codes_used: 0,
          books_used: 0,
          campaigns_used: 0,
          scans_this_month: 0,
          seats_used: 1,
          ai_requests_used: 0
        };
        setSubscription(defaultSub);
        setPlanLimits(getDefaultPlanLimits('free'));
      }
    } catch (error) {
      console.error('🔍 Plan Limits Debug: Failed to load plan data:', error);
      // Set default values on error
      const defaultSub = {
        id: 'default',
        user_id: 'default', 
        plan: 'free',
        status: 'active',
        qr_codes_used: 0,
        books_used: 0,
        campaigns_used: 0,
        scans_this_month: 0,
        seats_used: 1,
        ai_requests_used: 0
      };
      setSubscription(defaultSub);
      setPlanLimits(getDefaultPlanLimits('free'));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPlanLimits = (plan: string) => {
    const defaults: Record<string, PlanLimits> = {
      free: { plan: 'free', qr_codes_limit: 2, books_limit: 1, campaigns_limit: 0, scans_limit: -1, seats_limit: 1, ai_requests_limit: 0 },
      starter: { plan: 'starter', qr_codes_limit: 10, books_limit: 1, campaigns_limit: 5, scans_limit: -1, seats_limit: 1, ai_requests_limit: 0 },
      growth: { plan: 'growth', qr_codes_limit: 100, books_limit: 5, campaigns_limit: 20, scans_limit: -1, seats_limit: 5, ai_requests_limit: 5 },
      business: { plan: 'business', qr_codes_limit: 500, books_limit: 25, campaigns_limit: 100, scans_limit: -1, seats_limit: 10, ai_requests_limit: 20 }
    };
    return defaults[plan] || defaults.free;
  };

  const canCreateQRCode = () => {
    if (!subscription || !planLimits) {
      console.log('🔍 Plan Limits Debug: canCreateQRCode - data not loaded yet, allowing creation');
      return true; // Allow if data not loaded yet
    }
    const canCreate = subscription.qr_codes_used < planLimits.qr_codes_limit;
    console.log('🔍 Plan Limits Debug: canCreateQRCode -', {
      qr_codes_used: subscription.qr_codes_used,
      qr_codes_limit: planLimits.qr_codes_limit,
      canCreate
    });
    return canCreate;
  };

  const canCreateBook = () => {
    if (!subscription || !planLimits) {
      console.log('🔍 Plan Limits Debug: canCreateBook - data not loaded yet, allowing creation');
      return true; // Allow if data not loaded yet
    }
    const canCreate = subscription.books_used < planLimits.books_limit;
    console.log('🔍 Plan Limits Debug: canCreateBook -', {
      books_used: subscription.books_used,
      books_limit: planLimits.books_limit,
      canCreate
    });
    return canCreate;
  };

  const canCreateCampaign = () => {
    if (!subscription || !planLimits) {
      console.log('🔍 Plan Limits Debug: canCreateCampaign - data not loaded yet, allowing creation');
      return true; // Allow if data not loaded yet
    }
    // -1 means unlimited
    if (planLimits.campaigns_limit === -1) return true;
    
    const canCreate = subscription.campaigns_used < planLimits.campaigns_limit;
    console.log('🔍 Plan Limits Debug: canCreateCampaign -', {
      campaigns_used: subscription.campaigns_used,
      campaigns_limit: planLimits.campaigns_limit,
      canCreate
    });
    return canCreate;
  };

  const checkQRCodeLimitAndRedirect = () => {
    const canCreate = canCreateQRCode();
    console.log('🔍 Plan Limits Debug: checkQRCodeLimitAndRedirect - canCreate:', canCreate);
    if (!canCreate) {
      console.log('🔍 Plan Limits Debug: Redirecting to pricing page due to QR code limit');
      // Redirect to pricing page
      window.location.href = '/pricing';
      return false;
    }
    return true;
  };

  const checkBookLimitAndRedirect = () => {
    const canCreate = canCreateBook();
    console.log('🔍 Plan Limits Debug: checkBookLimitAndRedirect - canCreate:', canCreate);
    if (!canCreate) {
      console.log('🔍 Plan Limits Debug: Redirecting to pricing page due to book limit');
      // Redirect to pricing page
      window.location.href = '/pricing';
      return false;
    }
    return true;
  };

  const checkCampaignLimitAndRedirect = () => {
    const canCreate = canCreateCampaign();
    console.log('🔍 Plan Limits Debug: checkCampaignLimitAndRedirect - canCreate:', canCreate);
    if (!canCreate) {
      console.log('🔍 Plan Limits Debug: Redirecting to pricing page due to campaign limit');
      // Redirect to pricing page
      window.location.href = '/pricing';
      return false;
    }
    return true;
  };

  const getQRUsageInfo = () => {
    if (!subscription || !planLimits) return null;
    return {
      used: subscription.qr_codes_used,
      limit: planLimits.qr_codes_limit,
      remaining: planLimits.qr_codes_limit - subscription.qr_codes_used,
      percentage: (subscription.qr_codes_used / planLimits.qr_codes_limit) * 100
    };
  };

  const getBookUsageInfo = () => {
    if (!subscription || !planLimits) return null;
    return {
      used: subscription.books_used,
      limit: planLimits.books_limit,
      remaining: planLimits.books_limit - subscription.books_used,
      percentage: (subscription.books_used / planLimits.books_limit) * 100
    };
  };

  const getCampaignUsageInfo = () => {
    if (!subscription || !planLimits) return null;
    return {
      campaigns_used: subscription.campaigns_used,
      campaigns_limit: planLimits.campaigns_limit,
      remaining: planLimits.campaigns_limit === -1 ? -1 : planLimits.campaigns_limit - subscription.campaigns_used,
      percentage: planLimits.campaigns_limit === -1 ? 0 : (subscription.campaigns_used / planLimits.campaigns_limit) * 100
    };
  };

  // New functions for feature access control
  const canAccessStatistics = () => {
    // If still loading, allow access (don't redirect)
    if (loading || !subscription) {
      console.log('🔍 Plan Limits Debug: canAccessStatistics - still loading, allowing access');
      return true;
    }
    // Free and Starter plans are restricted from statistics
    const hasAccess = subscription.plan !== 'free' && subscription.plan !== 'starter';
    console.log('🔍 Plan Limits Debug: canAccessStatistics -', {
      plan: subscription.plan,
      hasAccess
    });
    return hasAccess;
  };

  const canAccessReports = () => {
    // If still loading, allow access (don't redirect)
    if (loading || !subscription) {
      console.log('🔍 Plan Limits Debug: canAccessReports - still loading, allowing access');
      return true;
    }
    // Only free plan is restricted from reports
    const hasAccess = subscription.plan !== 'free';
    console.log('🔍 Plan Limits Debug: canAccessReports -', {
      plan: subscription.plan,
      hasAccess
    });
    return hasAccess;
  };

  const canUploadPDF = () => {
    // If still loading, allow access (don't redirect)
    if (loading || !subscription) {
      console.log('🔍 Plan Limits Debug: canUploadPDF - still loading, allowing access');
      return true;
    }
    // Only free plan is restricted from PDF uploads
    const hasAccess = subscription.plan !== 'free';
    console.log('🔍 Plan Limits Debug: canUploadPDF -', {
      plan: subscription.plan,
      hasAccess
    });
    return hasAccess;
  };

  const canCreateYouTubeQR = () => {
    if (!subscription) return true; // Allow if not loaded yet
    // Free plan can create YouTube QRs (basic feature)
    return true;
  };

  const canUseAIIdeas = () => {
    if (!subscription || !planLimits) {
      console.log('🔍 Plan Limits Debug: canUseAIIdeas - data not loaded yet, allowing access');
      return true; // Allow if data not loaded yet
    }
    const hasAccess = planLimits.ai_requests_limit > 0;
    console.log('🔍 Plan Limits Debug: canUseAIIdeas -', {
      plan: subscription.plan,
      ai_requests_limit: planLimits.ai_requests_limit,
      hasAccess
    });
    return hasAccess;
  };

  const getAIUsageInfo = () => {
    if (!subscription || !planLimits) return null;
    return {
      used: subscription.ai_requests_used || 0,
      limit: planLimits.ai_requests_limit,
      remaining: Math.max(0, planLimits.ai_requests_limit - (subscription.ai_requests_used || 0)),
      percentage: planLimits.ai_requests_limit > 0 
        ? ((subscription.ai_requests_used || 0) / planLimits.ai_requests_limit) * 100 
        : 0,
      reset_at: subscription.ai_requests_reset_at
    };
  };

  const checkFeatureAccessAndRedirect = (feature: 'statistics' | 'reports' | 'pdf') => {
    let hasAccess = false;
    
    switch (feature) {
      case 'statistics':
        hasAccess = canAccessStatistics();
        break;
      case 'reports':
        hasAccess = canAccessReports();
        break;
      case 'pdf':
        hasAccess = canUploadPDF();
        break;
    }

    if (!hasAccess) {
      console.log(`🔍 Plan Limits Debug: Redirecting to pricing page - ${feature} not available on ${subscription?.plan} plan`);
      window.location.href = '/pricing';
      return false;
    }
    return true;
  };

  const canUseWhiteLabel = () => {
    if (!subscription) return false
    return subscription.plan === 'business'
  }

  return {
    subscription,
    planLimits,
    loading,
    canCreateQRCode,
    canCreateBook,
    canCreateCampaign,
    checkQRCodeLimitAndRedirect,
    checkBookLimitAndRedirect,
    checkCampaignLimitAndRedirect,
    getQRUsageInfo,
    getBookUsageInfo,
    getCampaignUsageInfo,
    // New feature access functions
    canAccessStatistics,
    canAccessReports,
    canUploadPDF,
    canCreateYouTubeQR,
    canUseAIIdeas,
    getAIUsageInfo,
    checkFeatureAccessAndRedirect,
    canUseWhiteLabel,
    refresh: loadPlanData
  };
} 